import { useEffect, useRef, type ReactNode } from 'react';

/**
 * The box a data table sits in (theme.css `.table-wrap`).
 *
 * WHY THE SCROLL IS CONDITIONAL: `overflow-x: auto` makes this box the nearest
 * scrollport of everything inside it, and a sticky table header
 * (`table.data thead th`) then sticks to a box that never scrolls vertically —
 * i.e. it stops following the page. So the box is a scroll container only while
 * the table genuinely overflows it (the applications register is 13 columns
 * wide and always does), and a table that fits keeps its header on the page.
 *
 * The measurement runs per animation frame while the box or its table resizes —
 * a column that wraps differently, a font load, a viewport change — so the class
 * follows the content instead of a breakpoint.
 */
export function TableWrap({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    const table = box.querySelector('table');

    const apply = () => {
      box.classList.toggle('table-wrap--scroll', box.scrollWidth > box.clientWidth + 1);
    };
    apply();

    const observer = new ResizeObserver(apply);
    observer.observe(box);
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="table-wrap">
      {children}
    </div>
  );
}
